import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import {
  FiAlertCircle,
  FiArrowUpRight,
  FiCheckCircle,
  FiChevronRight,
  FiClipboard,
  FiFolder,
  FiLock,
  FiMessageSquare,
  FiUserCheck,
} from 'react-icons/fi';
import AppShell from './ui/AppShell';
import { SECTIONS, rightsOf, unitsOf, useSessionUser } from '../lib/permissions';
import { periodLabel, periodOf, titleOf, todayKey } from './schedule/scheduleModel';
import { authorOf, channelsOf, inChannel, isUnreadFor } from './messages/messageModel';
import { db } from '../lib/firebase';
import { unitConstraints } from '../lib/unitQuery';
import { usePatientDoctorContext } from './PatientDoctorContext';

const CARD = 'rounded-3xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]';
const LINK =
  'inline-flex items-center gap-1 rounded-full text-[13px] font-medium text-brand-600 no-underline transition hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40';

const STATUS_TONE = {
  Hospitalisé: 'bg-brand-50 text-brand-700',
  Opéré: 'bg-emerald-50 text-emerald-700',
  'Non Opéré': 'bg-amber-50 text-amber-700',
  'Opéré-Exeat': 'bg-slate-100 text-slate-600',
  'Opéré-transféré': 'bg-violet-50 text-violet-700',
  'Opéré-décédé': 'bg-black/[0.06] text-black/60',
};

const relative = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' });
const UNITS = [
  ['year', 31536000],
  ['month', 2592000],
  ['week', 604800],
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
];
const timeAgo = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return '';
  const seconds = (date.getTime() - Date.now()) / 1000;
  const unit = UNITS.find(([, size]) => Math.abs(seconds) >= size);
  return unit ? relative.format(Math.round(seconds / unit[1]), unit[0]) : 'à l’instant';
};

const today = () => {
  const text = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// Patients carry the unit (Cuomo / Ctcv) in `services`; other staff see every unit.
const patientScope = (service = '') => {
  if (service.startsWith('Cuomo')) return 'Cuomo';
  if (service === 'Ctcv') return 'Ctcv';
  return null;
};

// constraints: extra query filters (e.g. the account's units), null when nothing may be read.
const NO_FILTER = [];
const useRecentDocs = (enabled, name, field, count, constraints = NO_FILTER) => {
  const [state, setState] = useState({ status: 'loading', items: [] });
  useEffect(() => {
    if (!enabled) return undefined;
    if (!constraints) {
      setState({ status: 'ready', items: [] });
      return undefined;
    }
    let cancelled = false;
    getDocs(query(collection(db, name), ...constraints, orderBy(field, 'desc'), limit(count)))
      .then((snapshot) => {
        if (!cancelled) setState({ status: 'ready', items: snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error', items: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, name, field, count, constraints]);
  return state;
};

const usePatients = (enabled) => {
  const { patients, ensurePatients } = usePatientDoctorContext();
  const [status, setStatus] = useState('loading');
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    ensurePatients()
      .then(() => !cancelled && setStatus('ready'))
      .catch(() => !cancelled && setStatus('error'));
    return () => {
      cancelled = true;
    };
  }, [enabled, ensurePatients]);
  return { status, patients };
};

const Skeleton = ({ rows = 3 }) => (
  <div className="flex flex-col gap-[14px]" aria-hidden="true">
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className="flex items-center gap-[12px]">
        <span className="h-9 w-9 flex-none animate-pulse rounded-full bg-black/[0.06]" />
        <span className="flex-1">
          <span className="block h-3 w-2/3 animate-pulse rounded-full bg-black/[0.06]" />
          <span className="mt-2 block h-3 w-1/3 animate-pulse rounded-full bg-black/[0.04]" />
        </span>
      </div>
    ))}
  </div>
);

const Unavailable = ({ children }) => (
  <p className="m-0 flex items-center gap-2 rounded-2xl bg-mist px-[14px] py-[12px] text-[13.5px] text-black/50">
    <FiAlertCircle size={15} aria-hidden="true" className="flex-none" />
    {children}
  </p>
);

const Empty = ({ children }) => (
  <p className="m-0 rounded-2xl bg-mist px-[14px] py-[12px] text-[13.5px] text-black/50">{children}</p>
);

const Panel = ({ title, action, children, className = '' }) => (
  <section className={`${CARD} p-[20px] sm:p-6 ${className}`}>
    <div className="mb-[16px] flex items-center justify-between gap-[12px]">
      <h2 className="m-0 text-[16px] font-semibold tracking-tight text-ink">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const Stat = ({ label, value, hint, icon: Icon, dark = false }) => (
  <div className={`${dark ? 'rounded-3xl bg-[#111214] text-white shadow-[0_12px_32px_rgba(0,0,0,0.18)]' : CARD} p-[20px]`}>
    <div className="flex items-start justify-between gap-2">
      <span className={`text-[13px] font-medium ${dark ? 'text-white/60' : 'text-black/50'}`}>{label}</span>
      <span
        className={`flex h-8 w-8 flex-none items-center justify-center rounded-full ${
          dark ? 'bg-white/10 text-white' : 'bg-black/[0.04] text-black/55'
        }`}
      >
        <Icon size={15} aria-hidden="true" />
      </span>
    </div>
    <p className={`mb-0 mt-[14px] text-[32px] font-semibold leading-none tracking-tight tabular-nums ${dark ? 'text-white' : 'text-ink'}`}>
      {value}
    </p>
    <p className={`mb-0 mt-2 text-[12.5px] ${dark ? 'text-white/50' : 'text-black/45'}`}>{hint}</p>
  </div>
);

const Role = () => {
  const location = useLocation();
  const user = useSessionUser();
  const rights = rightsOf(user);
  const emailJustRegistered = Boolean(location.state && location.state.emailRegistered);
  const scope = patientScope(user.service);

  const { status: patientsStatus, patients } = usePatients(Boolean(rights.patients));
  const messageFilter = useMemo(() => unitConstraints(user), [user]);
  const messages = useRecentDocs(Boolean(rights.messages), 'messages', 'timestamp', 100, messageFilter);
  const gardes = useRecentDocs(Boolean(rights.gardes), 'gardeAstreinte', 'timestamp', 60);

  const scoped = useMemo(
    () => (scope ? patients.filter((patient) => patient.services === scope) : patients),
    [patients, scope]
  );
  const recentPatients = useMemo(
    () => [...scoped].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)).slice(0, 6),
    [scoped]
  );
  // Messages of the person's channels (one per unit).
  const channels = useMemo(() => channelsOf(user), [user]);
  const serviceMessages = useMemo(
    () => messages.items.filter((message) => channels.some((channel) => inChannel(message, channel))),
    [messages.items, channels]
  );
  const unread = serviceMessages.filter((message) => isUnreadFor(message, user.name)).length;

  const loadingPatients = patientsStatus === 'loading';
  const count = (predicate) => (loadingPatients ? '…' : patientsStatus === 'error' ? '—' : scoped.filter(predicate).length);
  const sections = SECTIONS.filter((section) => section.menu !== false && rights[section.id]);
  // The roster of the person's units in force today, else the next one, else the latest deposited.
  const garde = useMemo(() => {
    const mine = gardes.items.filter(
      (item) => user.role === 'Admin' || !item.service || unitsOf(item.service).some((name) => unitsOf(user.service).includes(name))
    );
    const today = todayKey();
    const dated = mine.map((item) => ({ item, period: periodOf(item) })).filter((entry) => entry.period);
    const current = dated.find((entry) => entry.period.start <= today && today <= entry.period.end);
    if (current) return { ...current, label: 'En cours' };
    const next = dated.filter((entry) => entry.period.start > today).sort((a, b) => a.period.start.localeCompare(b.period.start))[0];
    if (next) return { ...next, label: 'Prochain tableau' };
    return mine[0] ? { item: mine[0], period: null, label: 'Dernier document ajouté' } : null;
  }, [gardes.items, user.role, user.service]);

  return (
    <AppShell>
      <main className="mx-auto max-w-6xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
        <header className="flex flex-col gap-2">
          <p className="m-0 text-[13px] font-medium text-black/45">{today()}</p>
          <h1 className="m-0 text-[30px] font-semibold leading-tight tracking-tight text-ink sm:text-[36px]">
            Bonjour, {user.name}
          </h1>
          <p className="m-0 text-[15px] text-black/50">
            {[user.role, user.service].filter(Boolean).join(' · ')}
            {rights.patients && <span> — dossiers {scope ? `du service ${scope}` : 'de tous les services'}</span>}
          </p>
        </header>

        {emailJustRegistered && (
          <div
            role="status"
            className="mt-6 flex items-start gap-[12px] rounded-2xl bg-emerald-50 px-[20px] py-[16px] text-[14px] leading-relaxed text-emerald-900 ring-1 ring-emerald-200"
          >
            <FiCheckCircle size={18} aria-hidden="true" className="mt-0.5 flex-none text-emerald-600" />
            <span>
              Votre adresse <strong className="font-semibold">{user.email}</strong> est enregistrée. À chaque
              connexion, saisissez le code CTCV-CUOMO reçu à cette adresse : il reste valable 7 jours.
            </span>
          </div>
        )}

        {rights.patients && (
          <div className="mt-8 grid grid-cols-2 gap-[12px] lg:grid-cols-4 lg:gap-[16px]">
            <Stat dark label="Dossiers suivis" value={count(() => true)} hint={scope ? `Service ${scope}` : 'Tous les services'} icon={FiFolder} />
            <Stat label="Hospitalisés" value={count((p) => p.statut === 'Hospitalisé')} hint="Statut « Hospitalisé »" icon={FiUserCheck} />
            <Stat label="À valider" value={count((p) => p.validation === 'Non Validé')} hint="Dossiers non validés" icon={FiClipboard} />
            {rights.messages ? (
              <Stat
                label={
                  <>
                    <span className="sm:hidden">Non lus</span>
                    <span className="hidden sm:inline">Messages non lus</span>
                  </>
                }
                value={messages.status === 'loading' ? '…' : messages.status === 'error' ? '—' : unread}
                hint={user.role === 'Admin' ? 'Toutes les unités' : `Unité ${channels.join(', ')}`}
                icon={FiMessageSquare}
              />
            ) : (
              <Stat label="Opérés" value={count((p) => p.statut === 'Opéré')} hint="Statut « Opéré »" icon={FiCheckCircle} />
            )}
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-[16px] lg:grid-cols-3">
          {rights.patients && (
            <Panel
              title="Dossiers récents"
              className="lg:col-span-2"
              action={
                <Link to="/patients" className={LINK}>
                  Tout voir <FiChevronRight size={14} aria-hidden="true" />
                </Link>
              }
            >
              {loadingPatients ? (
                <Skeleton rows={5} />
              ) : patientsStatus === 'error' ? (
                <Unavailable>La liste des patients n’a pas pu être chargée.</Unavailable>
              ) : recentPatients.length === 0 ? (
                <Empty>Aucun dossier pour le moment.</Empty>
              ) : (
                <ul className="m-0 flex list-none flex-col p-0">
                  {recentPatients.map((patient) => (
                    <li key={patient._id} className="border-0 border-b-[1px] border-solid border-black/[0.05] last:border-b-0">
                      <Link
                        to={`/patients/${patient._id}`}
                        className="-mx-2 flex items-center gap-[12px] rounded-2xl px-2 py-[10px] text-ink no-underline transition hover:bg-black/[0.03] hover:text-ink"
                      >
                        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-black/[0.05] text-[13px] font-semibold text-black/60">
                          {(patient.nom || '?').charAt(0).toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[14.5px] font-medium">{patient.nom}</span>
                          <span className="block truncate text-[12.5px] text-black/45">
                            {[patient.dossierNumber && `Dossier ${patient.dossierNumber}`, patient.services, timeAgo(patient.updatedAt)]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </span>
                        {patient.statut && (
                          <span
                            className={`hidden flex-none rounded-full px-[10px] py-1 text-[12px] font-medium sm:inline-block ${
                              STATUS_TONE[patient.statut] || 'bg-black/[0.05] text-black/60'
                            }`}
                          >
                            {patient.statut}
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          )}

          <div className="flex flex-col gap-[16px]">
            {rights.messages && (
              <Panel
                title="Messages du service"
                action={
                  <Link to="/message" className={LINK}>
                    Ouvrir <FiChevronRight size={14} aria-hidden="true" />
                  </Link>
                }
              >
                {messages.status === 'loading' ? (
                  <Skeleton rows={3} />
                ) : messages.status === 'error' ? (
                  <Unavailable>Messages indisponibles pour le moment.</Unavailable>
                ) : serviceMessages.length === 0 ? (
                  <Empty>Aucun message dans votre service.</Empty>
                ) : (
                  <ul className="m-0 flex list-none flex-col gap-[14px] p-0">
                    {serviceMessages.slice(0, 4).map((message) => {
                      const isUnread = isUnreadFor(message, user.name);
                      return (
                        <li key={message.id} className="flex items-start gap-[10px]">
                          <span className="mt-1.5 flex h-2 w-2 flex-none rounded-full" aria-hidden="true">
                            {isUnread && <span className="h-2 w-2 rounded-full bg-brand-600" />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-baseline justify-between gap-2">
                              <span className="truncate text-[13.5px] font-semibold text-ink">{authorOf(message)}</span>
                              <span className="flex-none text-[12px] text-black/40">{timeAgo(message.timestamp)}</span>
                            </span>
                            <span className="mt-0.5 line-clamp-2 block text-[13.5px] leading-snug text-black/55">
                              {message.text || 'Pièce jointe'}
                            </span>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Panel>
            )}

            {rights.gardes && (
              <Panel
                title="Gardes et astreintes"
                action={
                  <Link to="/garde" className={LINK}>
                    Tout voir <FiChevronRight size={14} aria-hidden="true" />
                  </Link>
                }
              >
                {gardes.status === 'loading' ? (
                  <Skeleton rows={1} />
                ) : gardes.status === 'error' ? (
                  <Unavailable>Documents de garde indisponibles pour le moment.</Unavailable>
                ) : !garde ? (
                  <Empty>Aucun tableau de garde.</Empty>
                ) : (
                  <div>
                    <p className="m-0 text-[12px] font-medium uppercase tracking-[0.06em] text-black/40">{garde.label}</p>
                    <p className="mb-0 mt-1 break-words text-[14.5px] font-medium text-ink">{titleOf(garde.item, 'Tableau de garde')}</p>
                    <p className="mb-0 mt-0.5 text-[12.5px] text-black/45">{garde.period ? periodLabel(garde.period) : timeAgo(garde.item.timestamp)}</p>
                    <Link to="/garde" className={`${LINK} mt-[12px]`}>
                      Voir le tableau <FiChevronRight size={14} aria-hidden="true" />
                    </Link>
                  </div>
                )}
              </Panel>
            )}
          </div>
        </div>

        <section className="mt-10">
          <h2 className="m-0 text-[18px] font-semibold tracking-tight text-ink">Vos espaces</h2>
          <p className="mb-0 mt-1 text-[14px] text-black/45">Les sections ouvertes pour votre compte.</p>
          <div className="mt-[16px] grid grid-cols-1 gap-[12px] sm:grid-cols-2 xl:grid-cols-3">
            {sections.map(({ id, title, description, path, icon: Icon, tint, locked }) => (
              <Link
                key={id}
                to={path}
                className={`${CARD} group flex items-center gap-[14px] p-[16px] text-ink no-underline transition duration-300 hover:-translate-y-0.5 hover:text-ink hover:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_16px_32px_rgba(0,0,0,0.08)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-600/25`}
              >
                <span className={`flex h-11 w-11 flex-none items-center justify-center rounded-2xl text-white ${tint}`}>
                  <Icon size={20} aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 text-[15px] font-semibold">
                    {title}
                    {locked && <FiLock size={12} aria-label="Code d’accès demandé" className="text-black/35" />}
                  </span>
                  <span className="mt-0.5 block text-[13px] leading-snug text-black/50">{description}</span>
                </span>
                <FiArrowUpRight
                  size={17}
                  aria-hidden="true"
                  className="flex-none text-black/25 transition group-hover:text-black/50"
                />
              </Link>
            ))}
          </div>
        </section>
      </main>
    </AppShell>
  );
};

export default Role;
